import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { useCurrentOrg } from '../orgs/useCurrentOrg';
import { getReview, resumeReview, type PausedRun } from './api';

export function ReviewDetailPage() {
  const { runID = '' } = useParams<{ runID: string }>();
  const { org } = useCurrentOrg();
  const orgId = org?.id ?? '';
  const nav = useNavigate();
  const qc = useQueryClient();
  const [notes, setNotes] = useState('');

  const review = useQuery({
    queryKey: ['review', orgId, runID],
    queryFn: () => getReview(orgId, runID),
    enabled: !!orgId && !!runID,
  });

  const resume = useMutation({
    mutationFn: (decision: 'allow' | 'block') =>
      resumeReview(orgId, runID, { decision, notes: notes.trim() || undefined }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reviews', orgId] });
      nav('/reviews');
    },
  });

  if (review.isLoading) {
    return <p className="text-sm text-ink-tertiary">Loading review…</p>;
  }
  if (!review.data) {
    return (
      <Card>
        <CardBody>
          <p className="text-sm text-danger">Review not found or already resolved.</p>
          <Link to="/reviews" className="text-sm text-bit2me hover:underline">
            ← Back to queue
          </Link>
        </CardBody>
      </Card>
    );
  }

  const r = review.data;
  const reportMd = (r.pause_payload?.report_md as string) || '';
  const payload = (r.pause_payload?.payload as Record<string, unknown>) || {};

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title={r.pause_title || 'Review required'}
          description={`From ${r.agent_name} (${r.agent_template}). Paused ${new Date(r.paused_at).toLocaleString()}.`}
        />
        <CardBody className="space-y-4">
          <Section title="Objective">
            <p className="whitespace-pre-wrap text-sm text-ink-primary">{r.objective}</p>
          </Section>

          {reportMd && (
            <Section title="Report">
              <pre className="overflow-x-auto whitespace-pre-wrap rounded-md border border-line-soft bg-surface-soft/40 p-3 text-xs text-ink-primary">
                {reportMd}
              </pre>
            </Section>
          )}

          {Object.keys(payload).length > 0 && (
            <Section title="Structured payload">
              <pre className="overflow-x-auto rounded-md border border-line-soft bg-surface-soft/40 p-3 text-[11px] text-ink-secondary">
                {JSON.stringify(payload, null, 2)}
              </pre>
            </Section>
          )}

          <Section title="Reviewer notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Why are you approving / rejecting? The agent sees this in the next turn."
              rows={3}
              className="w-full rounded-md border border-line bg-surface-white p-2 text-sm"
            />
          </Section>

          {resume.error && (
            <p className="text-sm text-danger">{(resume.error as Error).message || 'Failed to resume'}</p>
          )}

          <div className="flex items-center justify-end gap-2">
            <Link
              to="/reviews"
              className="rounded-md border border-line-soft px-4 py-2 text-sm text-ink-secondary hover:bg-surface-soft"
            >
              Cancel
            </Link>
            <button
              type="button"
              onClick={() => resume.mutate('block')}
              disabled={resume.isPending}
              className="rounded-md border border-danger/30 px-4 py-2 text-sm font-medium text-danger hover:bg-danger/5 disabled:opacity-50"
            >
              Block
            </button>
            <Button onClick={() => resume.mutate('allow')} disabled={resume.isPending}>
              {resume.isPending ? 'Resuming…' : 'Approve & resume'}
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="mb-1 text-xs font-medium uppercase tracking-eyebrow text-ink-tertiary">{title}</p>
      {children}
    </div>
  );
}

export type _ReviewDetail = PausedRun; // re-export not actually used; keeps tree-shaker happy
